import {
  createContext,
  type ReactNode,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  matchPath,
  Route,
  Routes,
  useLocation,
} from "react-router-dom-v5-compat";
import { MockCheckoutError } from "@hopper-b2b/checkout";

import {
  ExperimentsProvider,
  useExperiment,
  useGetExperimentVariant,
} from "@hopper-b2b/experiments";
import I18nProvider, { getLang } from "@hopper-b2b/i18n";
import { Env, PortalCore } from "@hopper-b2b/portal";
import { installCssConfig } from "@hopper-b2b/themes";
import {
  type ISessionContextProps,
  SessionContextProvider,
  SessionStateEnum,
  TenantContextProvider,
  useDeviceTypes,
  useHotkeys,
} from "@hopper-b2b/utilities";
import {
  createGenerateClassName,
  StylesProvider,
  ThemeProvider,
} from "@material-ui/core/styles";
import clsx from "clsx";
import corePortalConfigFromBasicConfig from "../config";
import type { NubankSessionInfo } from "./api/session/startSession";
import { ProtectedRoute } from "./components/Auth";
import { AuthModule } from "./components/AuthModule";
import AxiosInterceptorWrapper from "./components/AxiosInterceptorWrapper";
import Body, { MaintenanceFlagVariants } from "./components/Body";
import { darkModeIcons, lightModeIcons } from "./components/Body/sharedIcons";
import { NubankErrorConfigViewer } from "./components/ErrorConfigViewer";
import { B2BGenericFooter } from "./components/Footer";
import Header from "./components/Header";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import { NubankChatSupportProvider } from "./components/NubankChatSupportProvider";
import { NubankExperimentsOverrideBridge } from "./components/NubankExperimentsOverrideBridge";
import { SettingsMenu } from "./components/SettingsMenu";
import { TermsAndConditionsWrapper } from "./components/TermsAndConditionsWrapper";
import { providedTranslations } from "./config/translations";
import { useViewportCssVars } from "./hooks/useViewportCssVars";
import { branding } from "./modules/branding";
import { muiDarkTheme, nubankDarkVariables } from "./modules/darkTheme";
import { muiTheme, nubankVariables } from "./modules/defaultTheme";
import {
  muiUltravioletaDarkTheme,
  nubankUltravioletaDarkVariables,
} from "./modules/ultravioletaDarkTheme";
import {
  muiUltravioletaTheme,
  nubankUltravioletaVariables,
} from "./modules/ultravioletaDefaultTheme";
import coreStore from "./store";
import {
  getColorModeCookie,
  isUserTypeCookieUltravioleta,
  useDarkModePreferred,
} from "./utils/colors";
import config from "./utils/config";
import {
  NubankFeatureFlagsContext,
  type NubankFeatureFlagsContextProps,
} from "./utils/featureFlagsContext";
import { useNavDepthTracker } from "./utils/navigation";
import {
  apiConfig,
  HIDDEN_FOOTER_PATHS,
  HIDDEN_HEADER_PATHS,
  HIDDEN_HEADER_PATHS_MOBILE,
  PATH_HOTELS_AVAILABILITY,
  POSITION_FIXED_FOOTER_PATHS,
  ROUTE_AUTH,
} from "./utils/urlPaths";

import "./App.css";
import "./fonts.scss";

// Nubank Feature Flags (copied from Uber)
export enum FEATURE_FLAGS {
  MAINTENANCE = "nubank-maintenance",

  // Fintech Flags
  AIR_CFAR = "nubank-cfar",
  AIR_CHFAR = "nubank-chfar",
  AIR_DISRUPTION = "nubank-disruption",
  AIR_DISRUPTION_EXERCISE_REBOOKING = "nubank-air-disruption-exercise-rebooking",
  AIR_MISSED_CONNECTION = "nubank-air-missed-connection",
  AIR_PRICE_FREEZE = "nubank-air-price-freeze",
  AIR_PRICE_WATCH = "FlightsPriceWatch",
  PRICE_PREDICTION = "nubank-price-prediction",
  PRICE_DROP_PROTECTION = "nubank-price-drop-protection",
  DISRUPTION_PRODUCT_AVAILABILITY = "DisruptionProductAvailability",
  NUBANK_DISRUPTION_FE = "nubank-disruption-fe",
  ENABLE_DISRUPTION_EXERCISE_REBOOK = "EnableDisruptionExerciseRebook",

  // Features
  AIR_EXCHANGE = "air-self-serve-exchange",
  APP_DARKMODE = "darkmode",
  AIR_SHOP_V4 = "nubank-air-shop-v4",
  LOADING_STEPS = "nubank-loading-steps",
  LODGING = "lodging",
  HFv2 = "ProvidersExperimentEnableHF",
  WALLET_CREDITS = "nubank-wallet-credits",
  WALLET_OFFERS = "nubank-wallet-offer",
  SHOW_LODGING_MAP = "lodging-map",
  LODGING_EXPRESS_CHECKOUT = "LodgingExpressCheckout",
  EXTRA_BAGS = "ExtraBags",
  FEATURED_OFFERS = "FeaturedWalletOffers",
  WALLET_OFFER_URLS = "WalletOfferUrls",
  ENHANCED_EARN_ENABLED = "EnhancedEarnEnabled",
  ENHANCED_EARN_POINTS_BACK_RATE = "EnhancedEarnPointsBackRate",
  EARN_PERCENTAGE = "EarnPercentage",
  VIP_SUPPORT = "vip-support",
  CENTRALIZED_ERROR_CONFIG = "nubank-centralized-error-config",
  NUPAY_DIRECT = "nupay-direct-enrollment",
  NO_POST_BOOKING_PROVIDERS = "no-post-booking-providers",
  COMMERCE_PLATFORM_ROLLOUT = "CommercePlatformNubankRollout",
  COMMERCE_PLATFORM_FLIGHTS_ROLLOUT = "CommercePlatformNubankFlightsRollout",
  COMMERCE_PLATFORM_FLIGHTS_MY_TRIPS_DETAILS = "CommercePlatformNubankFlightsMyTripDetails",
  DEBUG_MENU_ACCESS = "nubank-debug-menu-access",
  NUBANK_CSAT_SURVEY = "NubankCsatSurvey",
  EXTERNAL_TRIPS_LIST = "NubankExternalTrips",
}

export enum EXPERIMENTS {
  AIR_FINTECH_SELECTION = "nubank-air-fintech-selection",
  HIDE_AIR_ITINERARY_REVIEW = "nubank-air-hide-itinerary-review",
  HIDE_FARE_DETAILS = "nubank-air-hide-fare-details",
  SEAT_MAP_PROVIDER_HOPPER = "SeatMapProviderHopper",
  VOUCHERS_10 = "hotel-voucher-discount-2024-10-percent",
  VOUCHERS_20 = "hotel-voucher-discount-2024-20-percent",
  REPLACE_MATERIAL_UI = "ReplaceMaterialUI",
  RENDER_LODGING_STRIKETHROUGH_PRICE = "RenderLodgingStrikethroughPrice",
  RENDER_HOTEL_MAP_ENTRY_POINT = "RenderHotelMapEntryPoint",
  NUBANK_BLACK_FRIDAY = "NubankBlackFriday",
  MAX_INSTALLMENTS = "MaxNupayInstallments",
  RENDER_LODGING_SHOP_VALUE_PROPS = "RenderLodgingShopValueProps",
  RENDER_EXTRA_HOTEL_AMENITIES = "RenderExtraHotelAmenities",
  NUBANK_TRAVEL_INSURANCE = "NubankTravelInsurance",
  RENDER_HOTEL_MAP_AS_DEFAULT = "RenderHotelMapAsDefaultView",
  HOTEL_DETAILS_CTA_COPY = "HotelDetailsCtaCopy",
  ROOM_DETAILS_CTA_COPY = "RoomDetailsCtaCopy",
  INSTALLMENTS_CAMPAIGN = "InstallmentsCampaign",
  CROSS_SELL_CONFIRMATION_SCREEN = "CrossSellConfirmationScreen",
  NUBANK_CHAT_ENTRYPOINT = "nubank-chat-entrypoint",
  // Passenger form variant: "control" | "native-textfields"
  NATIVE_PASSENGER_FORM = "NativePassengerForm",
  BOOKING_STATUS_CALLOUT = "BookingStatusCallout",
}

// Uncomment to add partner experiments
// const PARTNER_EXPERIMENTS_QUERY_PARAM = "partner_experiments";
// const DEFAULT_PARTNER_EXPERIMENTS = "air_fintech_control";

// const PARTNER_EXPERIMENTS_SESSION_STORAGE_KEY = "b2bportal-partner-experiments";

const generateClassName = createGenerateClassName({
  productionPrefix: "ptBaseModule",
  seed: "ptBaseModule",
});

// TODO: Set default locale
const DEFAULT_LOCALE = getLang("pt-BR");

export const StyleContext = createContext({});

type UserInfoContext = {
  sessionInfo: NubankSessionInfo;
  updateSessionInfo: (info: NubankSessionInfo) => void;
  error: string;
  setError: (error: string) => void;
};

export const UserContext = createContext<Partial<UserInfoContext>>({});

const App = () => {
  const [sessionInfo, setSessionInfo] = useState<NubankSessionInfo>(null);
  const [error, _setError] = useState<string>("");
  const updateSessionInfo = (sessionInfo) => {
    setSessionInfo(sessionInfo);
  };
  const setError = (error: string) => {
    _setError(error);
  };

  const sessionContext: ISessionContextProps = useMemo(() => {
    const [firstName, lastName] =
      sessionInfo?.userInfoResponse?.name?.split(" ") || "";
    return {
      sessionType: SessionStateEnum.Authenticated,
      isLoggedIn: true,
      email: sessionInfo?.userInfoResponse?.email,
      firstName,
      lastName,
    };
  }, [sessionInfo]);

  useViewportCssVars();

  const partnerExperiments = useMemo(
    () => sessionInfo?.userInfoResponse?.partnerExperiments || "none",
    [sessionInfo]
  );
  // Assume we are in production if we aren't told otherwise
  const env = config?.ENV ?? Env.production;
  const isDebugEnv = env !== Env.production;

  return (
    // note: can remove this fallback when we move to the public/env.js strategy
    // <LoadScript googleMapsApiKey={config.googleMapsApiKey || ""}>
    <div>
      <I18nProvider
        defaultLng={DEFAULT_LOCALE}
        branding={branding}
        useGeneratedTranslations={true}
        enableDebugHook={isDebugEnv}
        providedTranslations={providedTranslations}
      >
        <UserContext.Provider
          value={{ sessionInfo, updateSessionInfo, setError, error }}
        >
          <SessionContextProvider sessionContext={sessionContext}>
            <ExperimentsProvider
              apiConfig={apiConfig}
              isLoggedIn={!!sessionInfo?.userInfoResponse?.email}
              partnerExperiments={partnerExperiments}
            >
              <NubankExperimentsOverrideBridge>
                <NubankFeatureFlagsWrapper>
                  <AppInternal />
                </NubankFeatureFlagsWrapper>
              </NubankExperimentsOverrideBridge>
            </ExperimentsProvider>
          </SessionContextProvider>
        </UserContext.Provider>
      </I18nProvider>
    </div>
    // </LoadScript>
  );
};

const AppInternal = () => {
  const location = useLocation();

  // Track SPA navigation depth so the flights back button can close the native
  // webview on deeplink entry (no in-app history). See NU-253 / NVH-733.
  useNavDepthTracker();

  const [theme, setTheme] = useState(muiTheme);

  const darkModePreferred = useDarkModePreferred();
  const [isDarkMode, setIsDarkMode] = useState(darkModePreferred);

  const { matchesMobile } = useDeviceTypes();

  const { sessionInfo } = useContext(UserContext);

  // TODO: update to use Nubank's method of notifying us that the user is UV
  const isUltravioletaUser = isUserTypeCookieUltravioleta();

  useEffect(() => {
    if (isUltravioletaUser) {
      if (isDarkMode) {
        setTheme(muiUltravioletaDarkTheme);
      } else {
        setTheme(muiUltravioletaTheme);
      }
    }
    if (isDarkMode) {
      setTheme(muiDarkTheme);
    }
  }, [isDarkMode, isUltravioletaUser]);

  const corePortalConfigTheme = useMemo(() => {
    return corePortalConfigFromBasicConfig(
      isDarkMode,
      isUltravioletaUser ? "uv" : "default"
    );
  }, [isDarkMode, isUltravioletaUser]);

  // NOTE : with corePortalConfigTheme pretty sure we dont need this anymore
  const changeTheme = useCallback(
    (isDark: boolean) => {
      installCssConfig(
        isUltravioletaUser
          ? isDark
            ? nubankUltravioletaDarkVariables
            : nubankUltravioletaVariables
          : isDark
            ? nubankDarkVariables
            : nubankVariables
      );
    },
    [isUltravioletaUser]
  );
  changeTheme(isDarkMode);

  const handleDarkModeChange = useCallback(
    (val: boolean) => {
      setIsDarkMode(val);
      changeTheme(val);
    },
    [changeTheme]
  );

  // Add hotkey F2 for switching quickly between themes in dev/staging
  useHotkeys("F2", () => {
    if (import.meta.env.MODE !== "production") {
      setIsDarkMode((prev) => !prev);
    }
  });

  useViewportCssVars();

  const [launchEventSent, setLaunchEventSent] = useState(false);

  const isWebview = useCallback(() => {
    try {
      return /WebView/.test(navigator.userAgent);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      return false;
    }
  }, []);

  const appClasses = useMemo((): string => {
    const classes = ["App-container"];
    if (isWebview()) {
      classes.push("App-Framed");
    }
    if (isDarkMode) {
      classes.push("dark-mode");
    } else {
      classes.push("light-mode");
    }
    if (isUltravioletaUser) {
      classes.push("ultravioleta");
    }
    return classes.join(" ");
  }, [isDarkMode, isUltravioletaUser, isWebview]);

  useEffect(() => {
    const cookiePresent = getColorModeCookie();
    if (cookiePresent) {
      document.body.classList.add(isDarkMode ? "dark-mode" : "light-mode");
      document.body.classList.remove(isDarkMode ? "light-mode" : "dark-mode");
    }
  }, [darkModePreferred, isDarkMode]);

  const matchPaths = useCallback(
    (arr: string[]) => arr.some((path) => matchPath(path, location.pathname)),
    [location.pathname]
  );

  const showHeader = useMemo(
    () =>
      !matchPaths(
        matchesMobile ? HIDDEN_HEADER_PATHS_MOBILE : HIDDEN_HEADER_PATHS
      ),
    [matchPaths, matchesMobile]
  );

  const showFooter = useMemo(
    () =>
      !matchPaths(HIDDEN_FOOTER_PATHS) &&
      !location.pathname.includes(PATH_HOTELS_AVAILABILITY),
    [matchPaths, location.pathname]
  );

  return (
    <StyleContext.Provider value={{ theme }}>
      <StylesProvider generateClassName={generateClassName}>
        <ThemeProvider theme={theme}>
          <AxiosInterceptorWrapper
            hopperSessionToken={sessionInfo?.hopperSessionToken}
            nubankSessionToken={sessionInfo?.nubankAccessToken}
          />
          <TenantContextProviderWithAssets isDarkMode={isDarkMode}>
            <TermsAndConditionsWrapper>
              <PortalCore config={corePortalConfigTheme} store={coreStore}>
                <NubankChatSupportProvider>
                  <div className={appClasses}>
                    <Routes>
                      <Route path={ROUTE_AUTH} element={<AuthModule />} />
                      {config?.ENV !== Env.production && (
                        <Route
                          path="/dev/error-config"
                          element={<NubankErrorConfigViewer />}
                        />
                      )}
                      <Route
                        path="/*"
                        element={
                          <ProtectedRoute>
                            <>
                              {sessionInfo?.isImpersonation ? (
                                <ImpersonationBanner />
                              ) : null}
                              <div className={clsx("App-content")}>
                                {showHeader ? <Header /> : null}
                                <Body
                                  theme={theme}
                                  isFirstSession={
                                    sessionInfo?.userInfoResponse.isFirstSession
                                  }
                                  launchEventSent={launchEventSent}
                                  setLaunchEventSent={setLaunchEventSent}
                                  onDarkModeChange={handleDarkModeChange}
                                />
                              </div>
                              {showFooter ? (
                                <B2BGenericFooter
                                  className={clsx("generic-footer", {
                                    "position-fixed":
                                      location.pathname &&
                                      POSITION_FIXED_FOOTER_PATHS.includes(
                                        location.pathname
                                      ),
                                  })}
                                />
                              ) : null}
                            </>
                          </ProtectedRoute>
                        }
                      />
                    </Routes>
                    <SettingsMenu />
                    {config?.ENV !== Env.production && <MockCheckoutError />}
                  </div>
                </NubankChatSupportProvider>
              </PortalCore>
            </TermsAndConditionsWrapper>
          </TenantContextProviderWithAssets>
        </ThemeProvider>
      </StylesProvider>
    </StyleContext.Provider>
  );
};

const TenantContextProviderWithAssets = ({
  children,
  isDarkMode,
}: {
  children: ReactNode | undefined;
  isDarkMode: boolean;
}) => {
  const lightIcons = {
    locationMarker: lightModeIcons.locationMarker,
    guest: lightModeIcons.user,
    multipleGuests: lightModeIcons.twoUsers,
    hotelIcon: lightModeIcons.hotelIcon,
    back: lightModeIcons.back,
    greenShieldCheck: lightModeIcons.greenShieldCheck,
    grayGuest: lightModeIcons.twoUsers,
    bed: lightModeIcons.bed,
    unavailable: lightModeIcons.unavailable,
    airplaneDouble: lightModeIcons.airplaneDouble,
    backWhite: lightModeIcons.backWhite,
    mapIcon: darkModeIcons.locationMarker,
    reload: lightModeIcons.reload,
    copy: lightModeIcons.copy,
  };
  const darkIcons = {
    locationMarker: darkModeIcons.locationMarker,
    guest: darkModeIcons.user,
    multipleGuests: darkModeIcons.twoUsers,
    hotelIcon: darkModeIcons.hotelIcon,
    back: darkModeIcons.back,
    greenShieldCheck: darkModeIcons.greenShieldCheck,
    grayGuest: darkModeIcons.twoUsers,
    bed: darkModeIcons.bed,
    unavailable: darkModeIcons.unavailable,
    airplaneDouble: lightModeIcons.airplaneDouble,
    backWhite: lightModeIcons.backWhite,
    mapIcon: darkModeIcons.locationMarker,
    reload: darkModeIcons.reload,
    copy: darkModeIcons.copy,
  };

  const tenantContext = {
    icons: isDarkMode ? darkIcons : lightIcons,
  };
  return (
    <TenantContextProvider tenantContext={tenantContext} children={children} />
  );
};

const NubankFeatureFlagsWrapper = ({ children }: PropsWithChildren) => {
  const initialNubankFeatureFlags = useNubankFeatureFlags();
  const [overrides, setOverrides] = useState<
    Partial<NubankFeatureFlagsContextProps>
  >({});

  const overrideFlag = useCallback((key: string, value: boolean | string) => {
    setOverrides((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetOverrides = useCallback(() => {
    setOverrides({});
  }, []);

  // Merge server values with local overrides
  // Apply defaults for flags where backend returns false but we want true by default
  const mergedFlags = useMemo(() => {
    const cleanedInitialFlags = Object.fromEntries(
      Object.entries(initialNubankFeatureFlags).map(([key, value]) => [
        key,
        value === undefined ? null : value,
      ])
    );

    // Apply default overrides for flags that should default to true
    // Only apply if there's no explicit user override
    const defaultsForMissingOverrides: Partial<NubankFeatureFlagsContextProps> =
      {};
    if (
      overrides.enableAirShopV4 === undefined &&
      cleanedInitialFlags.enableAirShopV4 === false
    ) {
      defaultsForMissingOverrides.enableAirShopV4 = true;
    }
    if (
      overrides.enableLodging === undefined &&
      cleanedInitialFlags.enableLodging === false
    ) {
      defaultsForMissingOverrides.enableLodging = true;
    }

    return {
      ...cleanedInitialFlags,
      ...defaultsForMissingOverrides,
      ...overrides,
    };
  }, [initialNubankFeatureFlags, overrides]);

  return (
    <NubankFeatureFlagsContext.Provider
      value={{
        ...mergedFlags,
        setOverride: overrideFlag,
        resetOverrides: resetOverrides,
      }}
    >
      {children}
    </NubankFeatureFlagsContext.Provider>
  );
};

const useNubankFeatureFlags = (): NubankFeatureFlagsContextProps => {
  // Maintenance flags
  const appMaintenance = useExperiment(
    FEATURE_FLAGS.MAINTENANCE,
    MaintenanceFlagVariants["disable-everything"]
  );
  const appExcludingTripsMaintenance = useExperiment(
    FEATURE_FLAGS.MAINTENANCE,
    MaintenanceFlagVariants["exclude-trips"]
  );
  const flightsMaintenance = useExperiment(
    FEATURE_FLAGS.MAINTENANCE,
    MaintenanceFlagVariants.flights
  );
  const hotelsMaintenance = useExperiment(
    FEATURE_FLAGS.MAINTENANCE,
    MaintenanceFlagVariants.hotels
  );
  const tripsMaintenance = useExperiment(
    FEATURE_FLAGS.MAINTENANCE,
    MaintenanceFlagVariants.trips
  );

  // Fintech Flags
  const enableCfar = useExperiment(FEATURE_FLAGS.AIR_CFAR);
  const enableChfar = useExperiment(FEATURE_FLAGS.AIR_CHFAR);
  const enableDisruption = useExperiment(FEATURE_FLAGS.AIR_DISRUPTION);
  const enableDisruptionExerciseRebooking = useExperiment(
    FEATURE_FLAGS.AIR_DISRUPTION_EXERCISE_REBOOKING
  );
  const enableMissedConnection = useExperiment(
    FEATURE_FLAGS.AIR_MISSED_CONNECTION
  );
  const enablePriceFreeze = useExperiment(FEATURE_FLAGS.AIR_PRICE_FREEZE);
  const enablePriceWatch = useExperiment(FEATURE_FLAGS.AIR_PRICE_WATCH);
  const enablePricePrediction = useExperiment(FEATURE_FLAGS.PRICE_PREDICTION);
  const enablePriceDropProtection = useExperiment(
    FEATURE_FLAGS.PRICE_DROP_PROTECTION
  );
  const enableDisruptionProductAvailability = useExperiment(
    FEATURE_FLAGS.DISRUPTION_PRODUCT_AVAILABILITY
  );
  const enableNubankDisruptionFE = useExperiment(
    FEATURE_FLAGS.NUBANK_DISRUPTION_FE
  );
  const enableDisruptionExerciseRebook = useExperiment(
    FEATURE_FLAGS.ENABLE_DISRUPTION_EXERCISE_REBOOK
  );

  // Features
  const enableAirExchange = useExperiment(FEATURE_FLAGS.AIR_EXCHANGE);
  const enableDarkMode = useExperiment(FEATURE_FLAGS.APP_DARKMODE);
  const enableAirShopV4 = useExperiment(FEATURE_FLAGS.AIR_SHOP_V4);
  const enableLoadingSteps = useExperiment(FEATURE_FLAGS.LOADING_STEPS);
  const enableLodging = useExperiment(FEATURE_FLAGS.LODGING);
  const enableHFv2 = useExperiment(FEATURE_FLAGS.HFv2);
  const enableWalletCredits = useExperiment(FEATURE_FLAGS.WALLET_CREDITS);
  const enableWalletOffers = useExperiment(FEATURE_FLAGS.WALLET_OFFERS);
  const enableLodgingMap = useExperiment(FEATURE_FLAGS.SHOW_LODGING_MAP);
  const enableLodgingExpressCheckout = useExperiment(
    FEATURE_FLAGS.LODGING_EXPRESS_CHECKOUT
  );
  const enableExtraBags = useExperiment(FEATURE_FLAGS.EXTRA_BAGS);
  const enableFeaturedOffers = useExperiment(FEATURE_FLAGS.FEATURED_OFFERS);
  const enableWalletOfferUrls = useExperiment(FEATURE_FLAGS.WALLET_OFFER_URLS);
  const enableEnhancedEarn = useExperiment(FEATURE_FLAGS.ENHANCED_EARN_ENABLED);
  const enableEnhancedEarnPointsBackRate = useExperiment(
    FEATURE_FLAGS.ENHANCED_EARN_POINTS_BACK_RATE
  );
  const enableEarnPercentage = useExperiment(FEATURE_FLAGS.EARN_PERCENTAGE);
  const enableVipSupport = useExperiment(FEATURE_FLAGS.VIP_SUPPORT);
  const enableCentralizedErrorConfig = useExperiment(
    FEATURE_FLAGS.CENTRALIZED_ERROR_CONFIG
  );
  const enableNupayDirect = useExperiment(FEATURE_FLAGS.NUPAY_DIRECT);
  const enableDebugMenuAccess = useExperiment(FEATURE_FLAGS.DEBUG_MENU_ACCESS);
  const enableCsatSurvey = useExperiment(FEATURE_FLAGS.NUBANK_CSAT_SURVEY);
  const enableExternalTripsList = useExperiment(
    FEATURE_FLAGS.EXTERNAL_TRIPS_LIST
  );

  // Experiments
  const hideAirItineraryReview = useExperiment(
    EXPERIMENTS.HIDE_AIR_ITINERARY_REVIEW
  );
  const hideFareDetails = useExperiment(EXPERIMENTS.HIDE_FARE_DETAILS);
  const enableFintechSelection = useExperiment(
    EXPERIMENTS.AIR_FINTECH_SELECTION
  );
  const seatsAvailable = useExperiment(EXPERIMENTS.SEAT_MAP_PROVIDER_HOPPER);
  const enableVouchers10 = useExperiment(EXPERIMENTS.VOUCHERS_10);
  const enableVouchers20 = useExperiment(EXPERIMENTS.VOUCHERS_20);
  const replaceMaterialUI = useExperiment(EXPERIMENTS.REPLACE_MATERIAL_UI);
  const renderLodgingStrikethroughPrice = useExperiment(
    EXPERIMENTS.RENDER_LODGING_STRIKETHROUGH_PRICE
  );
  const renderHotelMapEntryPoint = useExperiment(
    EXPERIMENTS.RENDER_HOTEL_MAP_ENTRY_POINT
  );
  const nubankMaxInstallments = useGetExperimentVariant(
    EXPERIMENTS.MAX_INSTALLMENTS
  );
  const enableBlackFridayExperiment = useExperiment(
    EXPERIMENTS.NUBANK_BLACK_FRIDAY
  );
  const enableInstallmentsCampaign = useGetExperimentVariant(
    EXPERIMENTS.INSTALLMENTS_CAMPAIGN
  );
  const renderLodgingShopValueProps = useGetExperimentVariant(
    EXPERIMENTS.RENDER_LODGING_SHOP_VALUE_PROPS
  );
  const renderHotelMapAsDefaultView = useExperiment(
    EXPERIMENTS.RENDER_HOTEL_MAP_AS_DEFAULT
  );
  const renderExtraHotelAmenities = useGetExperimentVariant(
    EXPERIMENTS.RENDER_EXTRA_HOTEL_AMENITIES
  );
  const enableTravelInsurance = useExperiment(
    EXPERIMENTS.NUBANK_TRAVEL_INSURANCE
  );
  const enableHotelDetailsCtaCopy = useExperiment(
    EXPERIMENTS.HOTEL_DETAILS_CTA_COPY
  );
  const roomDetailsCtaCopy = useGetExperimentVariant(
    EXPERIMENTS.ROOM_DETAILS_CTA_COPY
  );
  // Variants: "control" | "native-textfields"
  const useSimplePassengerFormModal = useGetExperimentVariant(
    EXPERIMENTS.NATIVE_PASSENGER_FORM
  );
  const enableBookingStatusCallout = useExperiment(
    EXPERIMENTS.BOOKING_STATUS_CALLOUT
  );

  return {
    // Maintenance
    appMaintenance,
    appExcludingTripsMaintenance,
    flightsMaintenance,
    hotelsMaintenance,
    tripsMaintenance,
    // Fintech
    enableCfar,
    enableChfar,
    enableDisruption,
    enableDisruptionExerciseRebooking,
    enableMissedConnection,
    enablePriceFreeze,
    enablePriceWatch,
    enablePricePrediction,
    enablePriceDropProtection,
    enableDisruptionProductAvailability,
    enableNubankDisruptionFE,
    enableDisruptionExerciseRebook,
    // Features
    enableAirExchange,
    enableDarkMode,
    enableAirShopV4,
    enableLoadingSteps,
    enableLodging,
    enableHFv2,
    enableWalletCredits,
    enableWalletOffers,
    enableLodgingMap,
    enableLodgingExpressCheckout,
    enableExtraBags,
    enableFeaturedOffers,
    enableWalletOfferUrls,
    enableEnhancedEarn,
    enableEnhancedEarnPointsBackRate,
    enableEarnPercentage,
    enableVipSupport,
    enableCentralizedErrorConfig,
    enableNupayDirect,
    enableCsatSurvey,
    enableExternalTripsList,
    // Debug / Testing
    enableDebugMenuAccess,
    disableTouchFix: false,
    // Experiments
    hideAirItineraryReview,
    hideFareDetails,
    enableFintechSelection,
    seatsAvailable,
    enableVouchers10,
    enableVouchers20,
    replaceMaterialUI,
    renderLodgingStrikethroughPrice,
    renderHotelMapEntryPoint,
    nubankMaxInstallments,
    enableBlackFridayExperiment,
    enableInstallmentsCampaign,
    renderLodgingShopValueProps,
    renderHotelMapAsDefaultView,
    renderExtraHotelAmenities,
    enableTravelInsurance,
    enableHotelDetailsCtaCopy,
    roomDetailsCtaCopy,
    useSimplePassengerFormModal,
    enableBookingStatusCallout,
  };
};

export default App;
